import { z } from 'zod'

export const ConversionGoalGroupKey = {
  custom_bundles: 'custom_bundles',
  standard_actions: 'standard_actions',
  engagement_signals: 'engagement_signals',
}

export const SelectionMode = {
  category: 'category',
  custom_bundle: 'custom_bundle',
}

export const ConversionTrackingStatus = {
  not_conversion_tracked: 'NOT_CONVERSION_TRACKED',
}

// Account-level warning keys. Trigger per key:
// - no_conversion_tracking: conversion_tracking_status is NOT_CONVERSION_TRACKED.
// - managed_by_another_manager: conversion_tracking_status is
//   CONVERSION_TRACKING_MANAGED_BY_ANOTHER_MANAGER.
// - no_biddable_goals: a smart-bidding strategy is in play
//   (meta.conversion_required) and the account has default conversion goals
//   but none biddable — so it only ever appears when the request carried a
//   bidding_strategy.
export const AccountWarningKey = {
  no_conversion_tracking: 'no_conversion_tracking',
  managed_by_another_manager: 'managed_by_another_manager',
  no_biddable_goals: 'no_biddable_goals',
}

const conversionGoalItemSchema = z
  .object({
    id: z.string(),
    resource_name: z.string(),
    type: z.string(),
    name: z.string(),
    category: z.string().nullable().optional(),
    origin: z.string().nullable().optional(),
    primary_for_goal: z.boolean().nullable().optional(),
    relevance: z.string().nullable().optional(),
    account_biddable: z.boolean().optional(),
    quality_tier: z.string().nullable().optional(),
    contains_actions: z.array(z.string()).nullable().optional(),
    contains_categories: z.array(z.string()).nullable().optional(),
    warnings: z.array(z.string()).optional(),
  })
  .passthrough()

const conversionGoalGroupSchema = z
  .object({
    group: z.string(),
    group_label: z.string(),
    items: z.array(conversionGoalItemSchema),
  })
  .passthrough()

const conversionTrackingBlockSchema = z
  .object({
    conversion_tracking_status: z.string().nullable().optional(),
    conversion_customer_id: z.string().nullable().optional(),
    is_cross_account: z.boolean().optional(),
    managed_by_another_manager: z.boolean().optional(),
  })
  .passthrough()

const conversionGoalWarningSchema = z
  .object({
    key: z.string(),
    message: z.string(),
  })
  .passthrough()

// What the account's own default biddable conversion goals optimize for,
// independent of any explicit per-campaign selection — null when the
// account has no biddable default goal at all.
export const AccountDefaultOrientation = {
  purchase: 'purchase',
  lead: 'lead',
  other: 'other',
}

const conversionGoalsMetaSchema = z
  .object({
    smart_bidding: z.boolean().optional(),
    conversion_required: z.boolean().optional(),
    campaign_type: z.string().nullable().optional(),
    bidding_strategy: z.string().nullable().optional(),
    account_default_orientation: z.string().nullable().optional(),
  })
  .passthrough()

export const conversionGoalsResponseSchema = z
  .object({
    conversion_tracking: conversionTrackingBlockSchema.optional(),
    selection_mode: z.string().nullable().optional(),
    goals: z.array(conversionGoalGroupSchema).optional(),
    warnings: z.array(conversionGoalWarningSchema).optional(),
    meta: conversionGoalsMetaSchema.optional(),
  })
  .passthrough()
